import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { dnaSchema } from '$/requests/UpsertDnaRequest.ts';
import { allHowDoYouLikeToTravel } from '$/_const/dna/HowDoYouLikeToTravel.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import { missingTranslationToReadable } from '@/utils/missingTranslationToReadable.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaImageChoiceCard from '@/components/functional/TravelDnaManipulationForm/shared/DnaImageChoiceCard.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

import slowAndRestfulImage from '@/assets/img/onboarding/pace/slow-and-restful.jpg';
import balancedImage from '@/assets/img/onboarding/pace/balanced.jpg';
import busyAndActiveImage from '@/assets/img/onboarding/pace/busy-and-active.jpg';
import nonStopAdventureImage from '@/assets/img/onboarding/pace/non-stop-adventure.jpg';

const yourPaceSchema = dnaSchema.pick({
  how_do_you_like_to_travel: true,
});

export type YourPaceSchema = z.infer<typeof yourPaceSchema>;

type YourPaceProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

const paceImages = [
  slowAndRestfulImage,
  balancedImage,
  busyAndActiveImage,
  nonStopAdventureImage,
] as const;

function YourPace({ onComplete, onCompleteWhenDirty }: YourPaceProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const selectedValue = contextData?.how_do_you_like_to_travel;

  const selectOption = (
    option: YourPaceSchema['how_do_you_like_to_travel']
  ) => {
    void (async () => {
      try {
        if (isPending) return;
        setIsPending(true);
        setError(null);

        const data = yourPaceSchema.parse({
          how_do_you_like_to_travel: option,
        });
        const context = { ...contextData, ...data };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_PACE,
          data,
          context,
        };

        if (selectedValue !== option) {
          setContextData((previous) => ({ ...previous, ...data }));
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourPace.header')}
      title={t('travelDnaPage.yourPace.subheader')}
      description={t('travelDnaPage.yourPace.description')}
    >
      <DnaError error={error} className="mt-6" />

      <div
        className="mt-6 grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-x-12 sm:gap-y-7"
        role="radiogroup"
        aria-label={t('travelDnaPage.yourPace.subheader')}
      >
        {allHowDoYouLikeToTravel.map((option, index) => {
          const selected = selectedValue === option;
          return (
            <DnaImageChoiceCard
              key={option}
              image={paceImages[index]}
              label={t(
                `travelDnaPage.howDoYouLikeToTravel.${option}`,
                missingTranslationToReadable(option)
              )}
              selected={selected}
              dimmed={Boolean(selectedValue) && !selected}
              disabled={isPending}
              onClick={() => selectOption(option)}
            />
          );
        })}
      </div>
    </DnaStepLayout>
  );
}

export default YourPace;
